// Data-driven schema for the extended Pawfile consultation form.
// Two top-level tabs: Examen clinic + Investigații paraclinice.
//
// Field types:
//   "text"     — single-line input
//   "textarea" — multi-line textarea
//   "select"   — dropdown with fixed options (opts array)
//   "radio2"   — two mutually exclusive buttons (a/b)
//   "lab"      — table of pre-populated laboratory parameter rows
//   "rapid_tests" — rapid tests grid (Pozitiv/Negativ + optional obs)

// -----------------------------------------------------------------------------
// EXAMEN CLINIC
// -----------------------------------------------------------------------------
// "Sumar" is presented as a simple multiline textbox at the top of the tab,
// outside the enable/disable mechanism. Every other section is toggleable.
export const EXAM_SECTIONS = [
  { id: "sumar", titleKey: "examSumar" },
  {
    id: "oftalmologie",
    titleKey: "examOftalmologie",
    groups: [
      {
        fields: [
          { key: "inspectie", labelKey: "fInspectie", type: "textarea" },
          { key: "acuitate_vizuala", labelKey: "fAcuitateVizuala", type: "textarea" },
          { key: "reflexe_fotopupilare", labelKey: "fReflexeFoto", type: "textarea" },
          { key: "orbite", labelKey: "fOrbite", type: "textarea" },
          { key: "pleoape", labelKey: "fPleoape", type: "textarea" },
          { key: "conjunctiva", labelKey: "fConjunctiva", type: "textarea" },
          { key: "sclere", labelKey: "fSclere", type: "textarea" },
          { key: "cornee", labelKey: "fCornee", type: "textarea" },
          { key: "camera_anterioara", labelKey: "fCameraAnterioara", type: "textarea" },
          { key: "iris", labelKey: "fIris", type: "textarea" },
          { key: "cristalin", labelKey: "fCristalin", type: "textarea" },
          { key: "corpul_vitros", labelKey: "fCorpulVitros", type: "textarea" },
          { key: "fund_ochi", labelKey: "fFundOchi", type: "textarea" },
          { key: "observatii", labelKey: "fObservatii", type: "textarea" },
        ],
      },
    ],
  },
  {
    id: "otologie", titleKey: "examOtologie",
    groups: [
      { titleKey: "gPavilionExtern", fields: [
        { key: "inspectie_pav", labelKey: "fInspectie", type: "textarea" },
        { key: "secretie_pav", labelKey: "fSecretie", type: "textarea" },
        { key: "palpare_pav", labelKey: "fPalpare", type: "textarea" },
      ]},
      { titleKey: "gCanalAuditiv", fields: [
        { key: "otoscopie", labelKey: "fOtoscopie", type: "textarea" },
        { key: "membrana_timpanica", labelKey: "fMembranaTimpanica", type: "textarea" },
      ]},
    ],
  },
  {
    id: "cavitate_bucala", titleKey: "examCavitateBucala",
    groups: [
      { titleKey: "gInspectiaGurii", fields: [
        { key: "buze_obraji", labelKey: "fBuzeObraji", type: "textarea" },
        { key: "orofaringe", labelKey: "fOrofaringe", type: "textarea" },
        { key: "mucoasa_bucala", labelKey: "fMucoasaBucala", type: "textarea" },
        { key: "valul_palatin", labelKey: "fValulPalatin", type: "textarea" },
        { key: "planseul_limba", labelKey: "fPlanseulLimba", type: "textarea" },
      ]},
      { titleKey: "gDinti", fields: [
        { key: "dinti", labelKey: "fDinti", type: "textarea" },
        { key: "gingii", labelKey: "fGingii", type: "textarea" },
        { key: "halitoza", labelKey: "fHalitoza", type: "textarea" },
      ]},
      { titleKey: "gEvaluareStomatologica", fields: [
        { key: "tartru", labelKey: "fTartru", type: "textarea" },
        { key: "gingivita", labelKey: "fGingivita", type: "textarea" },
        { key: "retractie_gingivala", labelKey: "fRetractieGingivala", type: "textarea" },
        { key: "evaluare_generala", labelKey: "fEvaluareGenerala", type: "textarea" },
      ]},
      { fields: [{ key: "observatii", labelKey: "fObservatii", type: "textarea" }] },
    ],
  },
  {
    id: "dermatologie", titleKey: "examDermatologie",
    groups: [
      { titleKey: "gPiele", fields: [
        { key: "piele_inspectie", labelKey: "fInspectie", type: "textarea" },
        { key: "piele_palpare", labelKey: "fPalpare", type: "textarea" },
        { key: "piele_elasticitate", labelKey: "fElasticitate", type: "textarea" },
        { key: "piele_miros", labelKey: "fMiros", type: "textarea" },
        { key: "piele_prurit", labelKey: "fPrurit", type: "textarea" },
        { key: "piele_wood", labelKey: "fLampaWood", type: "textarea" },
        { key: "piele_observatii", labelKey: "fObservatii", type: "textarea" },
      ]},
      { titleKey: "gBlana", fields: [
        { key: "blana_inspectie", labelKey: "fInspectie", type: "textarea" },
        { key: "blana_palpare", labelKey: "fPalpare", type: "textarea" },
        { key: "blana_observatii", labelKey: "fObservatii", type: "textarea" },
      ]},
      { titleKey: "gUnghii", fields: [
        { key: "unghii_inspectie", labelKey: "fInspectie", type: "textarea" },
        { key: "unghii_palpare", labelKey: "fPalpare", type: "textarea" },
        { key: "unghii_observatii", labelKey: "fObservatii", type: "textarea" },
      ]},
    ],
  },
  {
    id: "noduli_limfatici", titleKey: "examNoduliLimfatici",
    groups: [
      { titleKey: "gSubmandibulari", fields: [
        { key: "sub_status", labelKey: "fStatus", type: "radio2", opts: ["reactivi", "areactivi"] },
        { key: "sub_detalii1", labelKey: "fDetalii", type: "textarea" },
      ]},
      { titleKey: "gAxilari", fields: [
        { key: "ax_status", labelKey: "fStatus", type: "radio2", opts: ["reactivi", "areactivi"] },
        { key: "ax_detalii1", labelKey: "fDetalii", type: "textarea" },
      ]},
      { titleKey: "gPrescapulari", fields: [
        { key: "pre_status", labelKey: "fStatus", type: "radio2", opts: ["reactivi", "areactivi"] },
        { key: "pre_detalii1", labelKey: "fDetalii", type: "textarea" },
      ]},
      { titleKey: "gInghinali", fields: [
        { key: "ing_status", labelKey: "fStatus", type: "radio2", opts: ["reactivi", "areactivi"] },
        { key: "ing_detalii1", labelKey: "fDetalii", type: "textarea" },
      ]},
      { titleKey: "gPoplitei", fields: [
        { key: "pop_status", labelKey: "fStatus", type: "radio2", opts: ["reactivi", "areactivi"] },
        { key: "pop_detalii", labelKey: "fDetalii", type: "textarea" },
      ]},
    ],
  },
  {
    id: "sistem_digestiv", titleKey: "examSistemDigestiv",
    groups: [
      { titleKey: "gSistemDigestiv", fields: [
        { key: "apetit", labelKey: "fApetit", type: "textarea" },
        { key: "prehensiune", labelKey: "fPrehensiune", type: "textarea" },
        { key: "masticatie", labelKey: "fMasticatie", type: "textarea" },
        { key: "deglutitie", labelKey: "fDeglutitie", type: "textarea" },
        { key: "voma", labelKey: "fVoma", type: "textarea" },
        { key: "tranzit_gi", labelKey: "fTranzitGI", type: "textarea" },
        { key: "fecale", labelKey: "fFecale", type: "textarea" },
      ]},
      { titleKey: "gAbdomen", fields: [
        { key: "abd_inspectie", labelKey: "fInspectie", type: "textarea" },
        { key: "abd_palpare", labelKey: "fPalpare", type: "textarea" },
        { key: "abd_observatii", labelKey: "fObservatii", type: "textarea" },
      ]},
      { titleKey: "gGlandePerianale", fields: [
        { key: "gp_inspectie", labelKey: "fInspectie", type: "textarea" },
        { key: "gp_palpare", labelKey: "fPalpare", type: "textarea" },
        { key: "gp_observatii", labelKey: "fObservatii", type: "textarea" },
      ]},
    ],
  },
  {
    id: "sistem_respirator", titleKey: "examSistemRespirator",
    groups: [
      { titleKey: "gCaiSuperioare", fields: [
        { key: "cs_inspectie", labelKey: "fInspectie", type: "textarea" },
        { key: "cs_secretie", labelKey: "fSecretie", type: "textarea" },
        { key: "cs_palpare", labelKey: "fPalpare", type: "textarea" },
        { key: "cs_observatii", labelKey: "fObservatii", type: "textarea" },
      ]},
      { titleKey: "gLaringeTrahee", fields: [
        { key: "lt_inspectie", labelKey: "fInspectie", type: "textarea" },
        { key: "lt_auscultatie", labelKey: "fAuscultatie", type: "textarea" },
        { key: "lt_palpare", labelKey: "fPalpare", type: "textarea" },
        { key: "lt_observatii", labelKey: "fObservatii", type: "textarea" },
      ]},
      { titleKey: "gPulmoni", fields: [
        { key: "p_inspectie", labelKey: "fInspectie", type: "textarea" },
        { key: "p_auscultatie", labelKey: "fAuscultatie", type: "textarea" },
        { key: "p_percutie", labelKey: "fPercutie", type: "textarea" },
        { key: "p_frecventa", labelKey: "fFrecventaResp", type: "text" },
        { key: "p_observatii", labelKey: "fObservatii", type: "textarea" },
      ]},
    ],
  },
  {
    id: "sistem_cardiovascular", titleKey: "examSistemCardiovascular",
    groups: [
      { fields: [
        { key: "cv_auscultatie", labelKey: "fAuscultatie", type: "textarea" },
        { key: "cv_frecventa", labelKey: "fFrecventaCard", type: "text" },
        { key: "cv_tensiune", labelKey: "fTensiune", type: "text" },
        { key: "cv_puls", labelKey: "fPuls", type: "text" },
        { key: "cv_observatii", labelKey: "fObservatii", type: "textarea" },
        { key: "cv_grad_suflu", labelKey: "fGradSuflu", type: "select",
          opts: ["Grad I", "Grad II", "Grad III", "Grad IV", "Grad V", "Grad VI"] },
        { key: "cv_tril", labelKey: "fTrilPrecordial", type: "select", opts: ["Da", "Nu"] },
        { key: "cv_tip_suflu", labelKey: "fTipSuflu", type: "select", opts: ["Sistolic", "Continuu"] },
        { key: "cv_localizare", labelKey: "fLocalizareSuflu", type: "select",
          opts: ["PMI Stanga", "PMI Drept", "PMI Sternal", "Nu se aplica"] },
      ]},
    ],
  },
  {
    id: "osteoarticular", titleKey: "examOsteoarticular",
    groups: [
      { titleKey: "gSistemOsos", fields: [
        { key: "os_inspectie", labelKey: "fInspectie", type: "textarea" },
        { key: "os_palpare", labelKey: "fPalpare", type: "textarea" },
        { key: "os_percutie", labelKey: "fPercutie", type: "textarea" },
        { key: "os_aplomb", labelKey: "fAplomb", type: "textarea" },
        { key: "os_observatii", labelKey: "fObservatii", type: "textarea" },
      ]},
      { titleKey: "gSistemMuscular", fields: [
        { key: "mu_inspectie", labelKey: "fInspectie", type: "textarea" },
        { key: "mu_palpare", labelKey: "fPalpare", type: "textarea" },
        { key: "mu_percutie", labelKey: "fPercutie", type: "textarea" },
        { key: "mu_observatii", labelKey: "fObservatii", type: "textarea" },
      ]},
    ],
  },
  {
    id: "sistem_nervos", titleKey: "examSistemNervos",
    groups: [
      { fields: [
        { key: "sn_constienta", labelKey: "fStareConstiinta", type: "textarea" },
        { key: "sn_coordonare", labelKey: "fCoordonare", type: "textarea" },
        { key: "sn_comportament", labelKey: "fComportament", type: "textarea" },
        { key: "sn_reflexe", labelKey: "fReflexe", type: "textarea" },
        { key: "sn_observatii", labelKey: "fObservatii", type: "textarea" },
      ]},
    ],
  },
  {
    id: "sistem_urogenital", titleKey: "examSistemUrogenital",
    groups: [
      { titleKey: "gSistemUrogenital", fields: [
        { key: "ug_status", labelKey: "fStatus", type: "select",
          opts: ["Alterat", "Presupus Alterat", "Intact", "Necunoscut", "Neevaluat"] },
        { key: "ug_procedura", labelKey: "fTipProcedura", type: "select",
          opts: ["Cicatrice", "Tatuaj", "Varful Urechii"] },
      ]},
      { titleKey: "gAparatUrinar", fields: [
        { key: "au_palpare", labelKey: "fPalpare", type: "textarea" },
        { key: "au_mictiune", labelKey: "fMictiune", type: "textarea" },
        { key: "au_urina", labelKey: "fUrina", type: "textarea" },
        { key: "au_vezica", labelKey: "fVezica", type: "textarea" },
        { key: "au_observatii", labelKey: "fObservatii", type: "textarea" },
      ]},
      { titleKey: "gGenitalMascul", fields: [
        { key: "gm_penis", labelKey: "fPenis", type: "textarea" },
        { key: "gm_testicule", labelKey: "fTesticule", type: "textarea" },
        { key: "gm_prostata", labelKey: "fProstata", type: "textarea" },
        { key: "gm_observatii", labelKey: "fObservatii", type: "textarea" },
      ]},
      { titleKey: "gGenitalFemela", fields: [
        { key: "gf_vulva", labelKey: "fVulva", type: "textarea" },
        { key: "gf_calduri", labelKey: "fCicluCalduri", type: "textarea" },
        { key: "gf_observatii", labelKey: "fObservatii", type: "textarea" },
      ]},
    ],
  },
];

// -----------------------------------------------------------------------------
// LAB_DEFAULTS — default unit + reference intervals per parameter.
// Species-neutral canine adult reference ranges pulled from common clinical
// literature (IDEXX / Cornell / VIN). Admins can override any value from
// Settings → Reference ranges; the frontend prefers Settings values when
// present and falls back to these defaults so parameters never render blank.
// -----------------------------------------------------------------------------
export const LAB_DEFAULTS = {
  // Biochemistry has NO hardcoded defaults — it is driven entirely by the
  // administrator's Settings → Reference ranges (single source of truth).
  // Biochemistry and CBC/hematology have NO hardcoded defaults — both are
  // driven entirely by the administrator's Settings (single source of truth).
  // electrolytes: Settings-only (no hardcoded defaults).
  urinary_sediment: [
    ["Hialini", "/hpf", 0, 2],
    ["Granulari", "/hpf", 0, 2],
    ["De grăsime", "/hpf", 0, 0],
    ["Epiteliali", "/hpf", 0, 5],
    ["Eritocitari", "/hpf", 0, 5],
    ["Leucocitari", "/hpf", 0, 5],
    ["Leucocite", "/hpf", 0, 5],
    ["Eritrocite", "/hpf", 0, 5],
    ["Picaturi de grăsime", "/hpf", 0, 2],
    ["Cilindri Hematici", "/hpf", 0, 0],
    ["Cilindri Biliari", "/hpf", 0, 0],
    ["Scuamoase", "/hpf", 0, 5],
    ["Tranzitionale", "/hpf", 0, 5],
    ["Tubulare renale", "/hpf", 0, 0],
    ["Neoplazice", "/hpf", 0, 0],
    ["Fosfati (urina neutra / alcalina)", "/hpf", 0, 5],
    ["Urati (acidă/neutră)", "/hpf", 0, 5],
    ["Oxalati de Ca (acida / neutra / alcalina)", "/hpf", 0, 5],
    ["Carbonati de Ca (neutra / alcalina)", "/hpf", 0, 5],
    ["Urati de amoniu (acida / neutra)", "/hpf", 0, 5],
    ["Bilirubina (acida)", "/hpf", 0, 0],
    ["Leucina (acida)", "/hpf", 0, 0],
    ["Tirozina (acida)", "/hpf", 0, 0],
    ["Cisteina (acida)", "/hpf", 0, 0],
    ["Sulfati de Ca", "/hpf", 0, 0],
    ["Fosfati de Ca", "/hpf", 0, 5],
    ["Acid Uric", "/hpf", 0, 5],
    ["Mucus", "/hpf", 0, 3],
    ["Spermatozoizi", "/hpf", 0, 0],
    ["Bacterii", "/hpf", 0, 0],
    ["Hife", "/hpf", 0, 0],
    ["Paraziti", "/hpf", 0, 0],
  ],
  // coagulation & immunology: Settings-only (no hardcoded defaults).
};

// Rapid tests — Pozitiv/Negativ + optional observation
export const RAPID_TESTS_DUAL = [
  "FeLV", "FIV", "FPV", "Giardia", "Parvoviroza", "Distemper", "Corona",
  "Dirofilaria", "Microfilaria", "Borrelia", "Anaplasma", "Ehrlichia", "Rotavirus",
];

// Rapid tests — free text
export const RAPID_TESTS_TEXT = [
  "Hepatita Rubarth", "Lipaza pancreatica", "Pro BNP", "Toxoplasma", "Brucella",
  "Leishmania", "CIRD-3", "Leptospiroză", "Babesia Gibsoni", "Strip Glucoză",
  "Gluco-test", "Curbă glicemică", "Tensiune arterială", "Stimulare ACTH",
];

// -----------------------------------------------------------------------------
// INVESTIGAȚII PARACLINICE
// -----------------------------------------------------------------------------
export const PARACLINICAL_SECTIONS = [
  { id: "biochimie", titleKey: "parBiochimie", type: "lab", category: "biochemistry" },
  { id: "hematologie", titleKey: "parHematologie", type: "lab", category: "hematology" },
  { id: "electroliti", titleKey: "parElectroliti", type: "lab_with_obs",
    category: "electrolytes", obsKey: "observatii", obsLabelKey: "fObservatii" },
  { id: "teste_rapide", titleKey: "parTesteRapide", type: "rapid_tests" },
  {
    id: "examen_copro", titleKey: "parExamenCopro",
    groups: [
      { fields: [
        { key: "consistenta", labelKey: "fConsistentaCulMiros", type: "textarea" },
        { key: "metoda", labelKey: "fMetodaLucru", type: "textarea" },
        { key: "observatii", labelKey: "fObservatii", type: "textarea" },
        { key: "flotatie", labelKey: "fTestFlotatie", type: "select", opts: ["Negativ", "Pozitiv"] },
        { key: "rezultat", labelKey: "fRezultat", type: "select",
          opts: ["Viermi rotunzi", "Hookworms", "Whipworms", "Tapeworms", "Giardia", "Altele"] },
      ]},
    ],
  },
  {
    id: "analize_urina", titleKey: "parAnalizeUrina",
    groups: [
      { fields: [
        { key: "strip_urina", labelKey: "fStripUrina", type: "textarea" },
        { key: "data_recoltare", labelKey: "fDataRecoltare", type: "text" },
        { key: "data_examinare", labelKey: "fDataExaminare", type: "text" },
        { key: "depozitare", labelKey: "fDepozitare", type: "textarea" },
        { key: "conditii", labelKey: "fConditii", type: "textarea" },
        { key: "culoare", labelKey: "fCuloare", type: "text" },
        { key: "transparenta", labelKey: "fTransparenta", type: "text" },
        { key: "densitate", labelKey: "fDensitate", type: "text" },
        { key: "hemoglobina", labelKey: "fHemoglobinaUr", type: "text" },
        { key: "eritrocite", labelKey: "fEritrociteIntacte", type: "text" },
        { key: "urobilinogen", labelKey: "fUrobilinogen", type: "text" },
        { key: "bilirubina", labelKey: "fBilirubinaUr", type: "text" },
        { key: "proteina", labelKey: "fProteinaUr", type: "text" },
        { key: "nitriti", labelKey: "fNitriti", type: "text" },
        { key: "cetonici", labelKey: "fCorpiCetonici", type: "text" },
        { key: "acid_ascorbic", labelKey: "fAcidAscorbic", type: "text" },
        { key: "glucoza", labelKey: "fGlucozaUr", type: "text" },
        { key: "ph", labelKey: "fPh", type: "text" },
        { key: "leucocite", labelKey: "fLeucociteUr", type: "text" },
      ]},
    ],
  },
  { id: "sediment_urinar", titleKey: "parSedimentUrinar",
    type: "lab", category: "urinary_sediment", locked: true },
  {
    id: "oftalmologie_par", titleKey: "parOftalmologie",
    groups: [
      { titleKey: "gProceduriDiagnostic", fields: [
        { key: "ecografie", labelKey: "fEcografieOculara", type: "textarea" },
        { key: "tonometrie", labelKey: "fTonometrie", type: "textarea" },
        { key: "oftalmoscopie", labelKey: "fOftalmoscopie", type: "textarea" },
      ]},
      { titleKey: "gTestSchirmer", fields: [
        { key: "schirmer_st_c", labelKey: "fSchirmerStgCant", type: "text" },
        { key: "schirmer_dr_c", labelKey: "fSchirmerDrCant", type: "text" },
        { key: "schirmer_st_q", labelKey: "fSchirmerStgCalit", type: "text" },
        { key: "schirmer_dr_q", labelKey: "fSchirmerDrCalit", type: "text" },
      ]},
      { titleKey: "gTestFluoresceina", fields: [
        { key: "fluo_stg", labelKey: "fFluoresceinaStg", type: "textarea" },
        { key: "fluo_dr", labelKey: "fFluoresceinaDr", type: "textarea" },
      ]},
      { titleKey: "gTestFirRosu", fields: [
        { key: "fenol_stg", labelKey: "fFirRosuStg", type: "text" },
        { key: "fenol_dr", labelKey: "fFirRosuDr", type: "text" },
      ]},
      { titleKey: "gAltele", fields: [
        { key: "citologic", labelKey: "fCitologic", type: "textarea" },
        { key: "antibiograma", labelKey: "fAntibiograma", type: "textarea" },
      ]},
    ],
  },
  {
    id: "otologie_par", titleKey: "parOtologie",
    groups: [
      { fields: [
        { key: "recoltare", labelKey: "fRecoltareSecretie", type: "select", opts: ["Negativ", "Pozitiv"] },
        { key: "rezultat_sec", labelKey: "fRezultatSecretie", type: "select",
          opts: ["Paraziti Auriculari", "Bacterii", "Fungi"] },
        { key: "parazitologic", labelKey: "fParazitologic", type: "textarea" },
        { key: "citologic", labelKey: "fCitologic", type: "textarea" },
        { key: "antibiograma", labelKey: "fAntibiograma", type: "textarea" },
        { key: "observatii", labelKey: "fObservatii", type: "textarea" },
      ]},
    ],
  },
  {
    id: "raport_citologic", titleKey: "parRaportCitologic",
    groups: [
      { fields: [
        { key: "aspect", labelKey: "fAspectGeneral", type: "textarea" },
        { key: "moment", labelKey: "fMomentAparitiei", type: "textarea" },
        { key: "localizare", labelKey: "fLocalizare", type: "textarea" },
        { key: "dimensiune", labelKey: "fDimensiune", type: "textarea" },
        { key: "consistenta_c", labelKey: "fConsistenta", type: "textarea" },
        { key: "semne_clinice", labelKey: "fSemneClinice", type: "textarea" },
        { key: "ln", labelKey: "fLnSuperficiale", type: "textarea" },
        { key: "rezultate_teste", labelKey: "fRezultateTeste", type: "textarea" },
        { key: "observatii", labelKey: "fObservatii", type: "textarea" },
        { key: "data_recoltare", labelKey: "fDataRecoltare", type: "text" },
        { key: "mod_recoltare", labelKey: "fModRecoltare", type: "textarea" },
        { key: "metoda_col", labelKey: "fMetodaColorare", type: "textarea" },
        { key: "descriere", labelKey: "fDescriereCit", type: "textarea" },
        { key: "diagnostic", labelKey: "fDiagnostic", type: "textarea" },
        { key: "examen_clinic", labelKey: "fExamenClinic", type: "textarea" },
      ]},
    ],
  },
  {
    id: "buletin_dermatologic", titleKey: "parBuletinDermatologic",
    groups: [
      { titleKey: "gDetalii", fields: [
        { key: "date_leziune", labelKey: "fDateLeziune", type: "textarea" },
        { key: "semne", labelKey: "fSemneClinice", type: "textarea" },
        { key: "tratamente_ant", labelKey: "fTratamenteAnterioare", type: "textarea" },
        { key: "examen_clinic", labelKey: "fExamenClinic", type: "textarea" },
      ]},
      { titleKey: "gProceduriDiagnostic", fields: [
        { key: "mod_recoltare", labelKey: "fModRecoltare", type: "textarea" },
        { key: "mod_lucru", labelKey: "fModLucru", type: "textarea" },
        { key: "parazitologic", labelKey: "fParazitologic", type: "textarea" },
        { key: "citologic", labelKey: "fCitologic", type: "textarea" },
        { key: "micologic", labelKey: "fMicologic", type: "textarea" },
        { key: "trichograma", labelKey: "fTrichograma", type: "textarea" },
        { key: "bacteriologic", labelKey: "fBacteriologic", type: "textarea" },
        { key: "antibiograma", labelKey: "fAntibiograma", type: "textarea" },
        { key: "rezultat", labelKey: "fRezultat", type: "textarea" },
        { key: "rezultate_proc", labelKey: "fRezultateProceduri", type: "textarea" },
        { key: "recomandari", labelKey: "fRecomandari", type: "textarea" },
      ]},
      { titleKey: "gLampaWood", fields: [
        { key: "wood_obs", labelKey: "fObservatii", type: "textarea" },
        { key: "wood_rez", labelKey: "fRezultatWood", type: "select", opts: ["Negativ", "Pozitiv"] },
        { key: "wood_concl", labelKey: "fConcluziiWood", type: "textarea" },
      ]},
      { titleKey: "gRaclajCutanat", fields: [
        { key: "raclaj_loc", labelKey: "fLocalizare", type: "select",
          opts: ["Regiunea nazală", "Ureche dreaptă", "Ureche stangă",
                 "Periocular dreapta", "Periocular stanga", "Obraz stang",
                 "Obraz drept", "Regiune frontală", "Corp", "Membre"] },
        { key: "raclaj_rez", labelKey: "fRezultatRaclaj", type: "select", opts: ["Negativ", "Pozitiv"] },
        { key: "raclaj_concl", labelKey: "fConcluziiRaclaj", type: "select",
          opts: ["Demodex", "Râie", "Păduchi", "Altele"] },
        { key: "raclaj_obs", labelKey: "fObservatii", type: "textarea" },
      ]},
    ],
  },
  {
    id: "buletin_ecografic", titleKey: "parBuletinEcografic",
    groups: [
      { titleKey: "gBuletinEcografic", fields: [
        { key: "e_obs", labelKey: "fObservatii", type: "textarea" },
        { key: "e_ln", labelKey: "fLimfonoduri", type: "textarea" },
      ]},
      { titleKey: "gCavitateToracica", fields: [
        { key: "t_obs", labelKey: "fObservatii", type: "textarea" },
        { key: "t_cord", labelKey: "fCord", type: "textarea" },
        { key: "t_pulmoni", labelKey: "fPulmoni", type: "textarea" },
      ]},
      { titleKey: "gCavitateAbdominala", fields: [
        { key: "a_obs", labelKey: "fObservatii", type: "textarea" },
        { key: "a_stomac", labelKey: "fStomac", type: "textarea" },
        { key: "a_ficat", labelKey: "fFicat", type: "textarea" },
        { key: "a_colecist", labelKey: "fColecist", type: "textarea" },
        { key: "a_splina", labelKey: "fSplina", type: "textarea" },
        { key: "a_pancreas", labelKey: "fPancreas", type: "textarea" },
        { key: "a_i_gros", labelKey: "fIntGros", type: "textarea" },
        { key: "a_i_subt", labelKey: "fIntSubt", type: "textarea" },
        { key: "a_rinichi", labelKey: "fRinichi", type: "textarea" },
      ]},
      { titleKey: "gBazin", fields: [
        { key: "b_obs", labelKey: "fObservatii", type: "textarea" },
        { key: "b_vezica", labelKey: "fVezica", type: "textarea" },
        { key: "b_prostata", labelKey: "fProstata", type: "textarea" },
        { key: "b_uter", labelKey: "fUter", type: "textarea" },
        { key: "b_ovar", labelKey: "fOvar", type: "textarea" },
      ]},
    ],
  },
  {
    id: "buletin_radiologic", titleKey: "parBuletinRadiologic",
    groups: [
      { fields: [
        { key: "indicatie", labelKey: "fIndicatie", type: "textarea" },
        { key: "tehnica", labelKey: "fTehnica", type: "textarea" },
        { key: "interpretare", labelKey: "fInterpretareRad", type: "textarea" },
        { key: "observatii", labelKey: "fObservatii", type: "textarea" },
      ]},
    ],
  },
  {
    id: "dozare_hormoni", titleKey: "parDozareHormoni",
    groups: [
      { fields: [
        { key: "cortizol", labelKey: "fCortizol", type: "text" },
        { key: "t3", labelKey: "fT3", type: "text" },
        { key: "t4", labelKey: "fT4", type: "text" },
        { key: "tsh", labelKey: "fTSH", type: "text" },
        { key: "lipaza_sp", labelKey: "fLipazaSpecifica", type: "text" },
        { key: "progesteron", labelKey: "fProgesteron", type: "text" },
        { key: "testosteron", labelKey: "fTestosteron", type: "text" },
        { key: "fsh", labelKey: "fFSH", type: "text" },
        { key: "lh", labelKey: "fLH", type: "text" },
        { key: "prolactina", labelKey: "fProlactina", type: "text" },
        { key: "observatii", labelKey: "fObservatii", type: "textarea" },
      ]},
    ],
  },
  { id: "imunologie", titleKey: "parImunologie", type: "lab", category: "immunology" },
  { id: "teste_coagulare", titleKey: "parTesteCoagulare", type: "lab", category: "coagulation" },
  {
    id: "examen_microscopic", titleKey: "parExamenMicroscopic",
    groups: [
      { fields: [
        { key: "proba", labelKey: "fProbaColectata", type: "select",
          opts: ["Punctie Aspirativa cu Ac Fin", "Citologie Exfoliativa",
                 "Proba de Sange", "Biopsie", "Altele"] },
        { key: "interpretare", labelKey: "fInterpretare", type: "textarea" },
        { key: "observatii", labelKey: "fObservatii", type: "textarea" },
      ]},
    ],
  },
  {
    id: "antibiograma", titleKey: "parAntibiograma",
    groups: [{ fields: [{ key: "sensibilitate", labelKey: "fSensibilitate", type: "textarea" }] }],
  },
];

import { labCanonicalLoose } from "./labI18n";

// Build the rows for a lab category. Rows are generated dynamically from the
// Settings-configured reference ranges (`refItems`): configured parameters
// override the bundled defaults, and any NEW parameters added in Settings are
// appended automatically so they appear in consultations without manual entry.
// De-duplication uses a lenient canonical name so equivalent parameters written
// differently (e.g. Romanian default "Leucocite" vs seeded English "WBC") are
// treated as the same parameter instead of producing duplicate rows.
export function buildLabRows(category, refItems = []) {
  const canon = (name) => labCanonicalLoose(String(name || "").trim());

  // Settings-only categories (single source of truth): biochemistry and
  // CBC/hematology have NO hardcoded defaults, so configured parameters can
  // never be duplicated by a bundled list. Rows follow the administrator's
  // Settings order and each parameter appears exactly once.
  // CBC params are seeded under category "cbc" and admin-added ones under
  // "hematology" (see Settings.jsx), so both are treated as one bucket.
  const SETTINGS_ONLY = {
    biochemistry: ["biochemistry"],
    hematology: ["cbc", "hematology"],
    cbc: ["cbc", "hematology"],
    electrolytes: ["electrolytes"],
    coagulation: ["coagulation"],
    immunology: ["immunology"],
  };
  if (SETTINGS_ONLY[category]) {
    const cats = SETTINGS_ONLY[category];
    const seen = new Set();
    const rows = [];
    for (const r of (refItems || []).filter((x) => cats.includes(x.category))) {
      const pname = (r.parameter || "").trim();
      const key = canon(pname);
      if (!pname || !key || seen.has(key)) continue;
      seen.add(key);
      rows.push({
        parameter: r.parameter,
        result: "",
        unit: r.unit ?? "",
        ref_min: r.min_value ?? null,
        ref_max: r.max_value ?? null,
      });
    }
    return rows;
  }

  const matchesCat = (r) =>
    r.category === category;

  // Values can be OVERRIDDEN from the category and its seed alias ("cbc" <->
  // "hematology"). But only rows saved under the EXACT category are treated as
  // user-added and eligible to be APPENDED as new parameters — this prevents
  // the English-seeded "cbc" rows from duplicating the Romanian defaults.
  const overrideItems = (refItems || []).filter(matchesCat);
  const appendItems = (refItems || []).filter((r) => r.category === category);

  const settingsByCanon = new Map();
  for (const r of overrideItems) {
    const key = canon(r.parameter);
    if (key && !settingsByCanon.has(key)) settingsByCanon.set(key, r);
  }

  const defaults = LAB_DEFAULTS[category] || [];
  const used = new Set();
  const rows = defaults.map(([parameter, unit, min, max]) => {
    const key = canon(parameter);
    const cfg = settingsByCanon.get(key);
    used.add(key);
    return {
      parameter,
      result: "",
      unit: cfg?.unit ?? unit,
      ref_min: cfg?.min_value ?? min,
      ref_max: cfg?.max_value ?? max,
    };
  });

  for (const r of appendItems) {
    const key = canon(r.parameter);
    const pname = (r.parameter || "").trim();
    if (!pname || !key || used.has(key)) continue;
    used.add(key);
    rows.push({
      parameter: r.parameter,
      result: "",
      unit: r.unit ?? "",
      ref_min: r.min_value ?? null,
      ref_max: r.max_value ?? null,
    });
  }
  return rows;
}

export const LAB_CATEGORIES = [
  "biochemistry", "hematology", "electrolytes",
  "urinary_sediment", "coagulation", "immunology",
];
